import React, {
  useEffect, useMemo, useReducer, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import axios from 'axios';
import {
  Alert, Button, Select, Spin,
} from 'antd';
import './athenaEncounterSelector.scss';

const RECENT_WINDOW = 'recent_12_months';
const OLDER_WINDOW = 'older_12_to_60_months';
const EMPTY_STATE = 'No clinical encounters are available for this patient in the selected period. Progress Notes must be associated with an existing encounter.';
const LOAD_ERROR = 'Unable to load clinical encounters. Please try again.';

const initialRequestState = {
  error: false,
  loaded: false,
  loading: false,
  options: [],
  selectedEncounterId: undefined,
};

const requestStateReducer = (state, action) => {
  switch (action.type) {
    case 'request_started':
      return { ...initialRequestState, loading: true };
    case 'request_succeeded':
      return { ...initialRequestState, loaded: true, options: action.options };
    case 'request_failed':
      return { ...initialRequestState, error: true, loaded: true };
    case 'selection_changed':
      return { ...state, selectedEncounterId: action.value };
    case 'reset':
      return initialRequestState;
    default:
      return state;
  }
};

const cancelledRequest = (error, controller) => (
  controller.signal.aborted || axios.isCancel(error) || error?.name === 'CanceledError'
);

const stringValue = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return undefined;
  return String(value);
};

const AthenaEncounterSelector = ({
  active,
  credentialId,
  departmentId,
  onAnalytics,
  onChange,
  onProofChange,
  patientId,
  reloadKey,
}) => {
  const [{
    error, loaded, loading, options, selectedEncounterId,
  }, dispatchRequestState] = useReducer(requestStateReducer, initialRequestState);
  const [windowKey, setWindowKey] = useState(RECENT_WINDOW);
  const [attempt, setAttempt] = useState(0);
  const [anchorDate] = useState(() => moment().startOf('day'));
  const onChangeRef = useRef(onChange);
  const onAnalyticsRef = useRef(onAnalytics);
  const requestRef = useRef(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onAnalyticsRef.current = onAnalytics;
  }, [onAnalytics]);

  const dateWindow = useMemo(() => {
    if (windowKey === OLDER_WINDOW) {
      return {
        startDate: anchorDate.clone().subtract(60, 'months').format('YYYY-MM-DD'),
        endDate: anchorDate.clone().subtract(12, 'months').subtract(1, 'day').format('YYYY-MM-DD'),
      };
    }

    return {
      startDate: anchorDate.clone().subtract(12, 'months').format('YYYY-MM-DD'),
      endDate: anchorDate.format('YYYY-MM-DD'),
    };
  }, [anchorDate, windowKey]);

  useEffect(() => {
    requestRef.current?.abort();
    dispatchRequestState({ type: 'reset' });
    onChangeRef.current?.(undefined);
    onProofChange(undefined);

    if (!active || !credentialId || !patientId || !departmentId) {
      return undefined;
    }

    const controller = new AbortController();
    requestRef.current = controller;
    dispatchRequestState({ type: 'request_started' });

    axios.get('/api/v1/emr_options', {
      params: {
        emr_integration_credential_id: credentialId,
        source: 'athena/encounters',
        patient_id: patientId,
        department_id: departmentId,
        start_date: dateWindow.startDate,
        end_date: dateWindow.endDate,
      },
      signal: controller.signal,
    }).then((response) => {
      if (controller.signal.aborted) return;
      dispatchRequestState({
        type: 'request_succeeded',
        options: Array.isArray(response.data?.options) ? response.data.options : [],
      });
    }).catch((requestError) => {
      if (cancelledRequest(requestError, controller)) return;
      dispatchRequestState({ type: 'request_failed' });
      onAnalyticsRef.current?.('athena_encounter_load_failed', {
        emr: 'athena', workflow: 'send_to_chart', window: windowKey, outcome: 'error',
      });
    });

    return () => controller.abort();
  }, [active, attempt, credentialId, dateWindow, departmentId, onProofChange, patientId, reloadKey, windowKey]);

  const handleSelectionChange = (value) => {
    dispatchRequestState({ type: 'selection_changed', value });
    const selectedOption = options.find((option) => String(option.value) === String(value));
    const encounterId = stringValue(selectedOption?.metadata?.encounter_id ?? selectedOption?.value);
    const selectedDepartmentId = stringValue(selectedOption?.metadata?.department_id ?? departmentId);
    const appointmentId = stringValue(selectedOption?.metadata?.appointment_id);
    const encounterProof = stringValue(selectedOption?.metadata?.encounter_proof);

    if (!encounterId || !selectedDepartmentId || !encounterProof) {
      onChangeRef.current?.(undefined);
      onProofChange(undefined);
      onAnalyticsRef.current?.('athena_encounter_selection_changed', {
        emr: 'athena', workflow: 'send_to_chart', window: windowKey, outcome: 'cleared',
      });
      return;
    }

    onChangeRef.current?.({
      athena_encounter: {
        version: 1,
        encounter_id: encounterId,
        department_id: selectedDepartmentId,
        ...(appointmentId ? { appointment_id: appointmentId } : {}),
      },
    });
    onProofChange(encounterProof);
    onAnalyticsRef.current?.('athena_encounter_selection_changed', {
      emr: 'athena', workflow: 'send_to_chart', window: windowKey, outcome: 'selected',
    });
  };

  const handleRetry = () => {
    onAnalyticsRef.current?.('athena_encounter_retry_clicked', {
      emr: 'athena', workflow: 'send_to_chart', window: windowKey, outcome: 'requested',
    });
    setAttempt((current) => current + 1);
  };

  const handleWindowChange = () => {
    const nextWindow = windowKey === RECENT_WINDOW ? OLDER_WINDOW : RECENT_WINDOW;
    if (nextWindow === OLDER_WINDOW) {
      onAnalyticsRef.current?.('athena_encounter_older_search_clicked', {
        emr: 'athena', workflow: 'send_to_chart', window: nextWindow, outcome: 'requested',
      });
    }
    setWindowKey(nextWindow);
  };

  if (!active) return null;

  const prerequisitesPresent = Boolean(credentialId && patientId && departmentId);

  return (
    <div className="athena-encounter-selector">
      <div className="athena-encounter-selector__label">
        Encounter / Visit
        {' '}
        <span aria-hidden="true">*</span>
      </div>
      {!prerequisitesPresent && (
        <Alert
          type="info"
          showIcon
          message="Select an Athena patient and Department to load encounters."
        />
      )}
      {prerequisitesPresent && loading && <Spin tip="Loading clinical encounters..." />}
      {prerequisitesPresent && !loading && error && (
        <Alert
          type="error"
          showIcon
          message={LOAD_ERROR}
          action={<Button size="small" onClick={handleRetry}>Retry</Button>}
        />
      )}
      {prerequisitesPresent && !loading && !error && loaded && options.length === 0 && (
        <Alert type="info" showIcon message={EMPTY_STATE} />
      )}
      {prerequisitesPresent && !loading && !error && options.length > 0 && (
        <Select
          id="athena-encounter-select"
          aria-label="Encounter / Visit"
          showSearch
          allowClear
          value={selectedEncounterId}
          onChange={handleSelectionChange}
          optionFilterProp="children"
          getPopupContainer={() => document.body}
          dropdownClassName="ehr-select-dropdown"
          placeholder="Select an existing encounter"
        >
          {options.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      )}
      {prerequisitesPresent && !loading && !error && loaded && (
        <Button type="link" className="athena-encounter-selector__window" onClick={handleWindowChange}>
          {windowKey === RECENT_WINDOW ? 'Search older visits' : 'Back to recent visits'}
        </Button>
      )}
    </div>
  );
};

AthenaEncounterSelector.propTypes = {
  active: PropTypes.bool,
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  departmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onAnalytics: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onProofChange: PropTypes.func.isRequired,
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  reloadKey: PropTypes.number,
};

AthenaEncounterSelector.defaultProps = {
  active: false,
  credentialId: undefined,
  departmentId: undefined,
  onAnalytics: undefined,
  patientId: undefined,
  reloadKey: 0,
};

export default AthenaEncounterSelector;
